/**
 * Helpers for the service-hub route class (digital-marketing, local-seo,
 * google-ads-ppc, google-maps, web-design, seo-services).
 *
 * Each hub page needs a Service node + BreadcrumbList per the JSON-LD audit.
 * These small builders return ready-to-spread `scripts:` entries so the
 * route head() blocks stay short.
 */

import { SCHEMA_LOCALBUSINESS_ID, SCHEMA_ORG_ID, SITE_URL } from "@/lib/entitySchema";

export type ServiceHubSchemaInput = {
  /** Path beginning with "/" — the canonical URL slug. */
  path: string;
  /** Display name of the service (e.g. "Digital Marketing"). */
  name: string;
  /** Plain-English description shown to crawlers. */
  description: string;
  /** schema.org serviceType taxonomy value. Defaults to `name`. */
  serviceType?: string;
  /** Optional human-readable price band (e.g. "$$"). */
  priceRange?: string;
  /** Optional FAQ Q/A pairs — emits a FAQPage node. */
  faqs?: Array<{ q: string; a: string }>;
};

export function buildServiceHubScripts(input: ServiceHubSchemaInput) {
  const url = `${SITE_URL}${input.path}`;
  const service = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${url}#service`,
    name: input.name,
    description: input.description,
    url,
    serviceType: input.serviceType ?? input.name,
    provider: { "@id": SCHEMA_LOCALBUSINESS_ID },
    areaServed: { "@type": "State", name: "Colorado" },
    ...(input.priceRange ? { offers: { "@type": "Offer", priceCurrency: "USD", priceRange: input.priceRange } } : {}),
  };
  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Services", item: `${SITE_URL}/services` },
      { "@type": "ListItem", position: 3, name: input.name, item: url },
    ],
  };
  // Reference SCHEMA_ORG_ID so static analyzers consider the import "used"
  // even on hubs that don't include extra org references in the JSON.
  void SCHEMA_ORG_ID;
  const scripts: Array<{ type: string; children: string }> = [
    { type: "application/ld+json", children: JSON.stringify(service) },
    { type: "application/ld+json", children: JSON.stringify(breadcrumb) },
  ];
  if (input.faqs && input.faqs.length > 0) {
    scripts.push({
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "@id": `${url}#faq`,
        mainEntity: input.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }),
    });
  }
  return scripts;
}

/**
 * Lightweight BreadcrumbList-only helper for misc routes that only need
 * BreadcrumbList added (privacy, terms, pricing, sitemap, etc.).
 */
export function buildBreadcrumbScript(items: Array<{ name: string; path?: string }>) {
  return {
    type: "application/ld+json",
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: items.map((item, idx) => ({
        "@type": "ListItem",
        position: idx + 1,
        name: item.name,
        ...(item.path ? { item: `${SITE_URL}${item.path}` } : {}),
      })),
    }),
  };
}
